// Pure helpers for GP-report / outbound-letter data shapes on a
// `client_appointment`. Kept separate from `services/gpReport.js` so consumers
// that only need to filter or inspect data don't pull in axios / firebase.

// Lifecycle status of a PMS-sourced outbound letter, mirrored on each
// `gp_reports[]` entry by the backend GP-report webhook handler.
// Kept here so the UI stays in sync with the backend constant of the same
// name (see `workflow-multi-api/src/utils/gpReportShape.js`).
export const OUTBOUND_LETTER_STATUS = Object.freeze({
  VERIFIED: "verified",
  ARCHIVED: "archived",
});

// `secureFileStatus` on a `gp_reports[]` entry, written by the backend when it
// creates the letter's Secure File link (SE-646). Entries synced before that
// have no status.
export const OUTBOUND_LETTER_SECURE_FILE_STATUS = Object.freeze({
  READY: "READY",
  FAILED: "FAILED",
});

// What the Outbound Letters row shows for the letter's Secure File link.
export const OUTBOUND_LETTER_LINK_STATE = Object.freeze({
  NONE: "none",
  ACTIVE: "active",
  FAILED: "failed",
  EXPIRED: "expired",
});

// The row's link: `{ secureFileToken, secureFileStatus }` from its
// `gp_reports[]` entry, or the result of a Retry made from the panel while the
// entry (from the parent's possibly stale `ca`) still shows the failure that
// was retried. The override records the entry's event `time` at retry time
// (`basisTime`) and only applies while that is unchanged and the entry is not
// READY: once the entry is READY, or a later event (e.g. an addendum whose
// download failed) has changed it, the entry is the truth again.
export const resolveOutboundLetterLink = (report, override) => {
  const own = {
    secureFileToken: (report && report.secureFileToken) || null,
    secureFileStatus: (report && report.secureFileStatus) || null,
  };
  const stillAsRetried =
    override &&
    own.secureFileStatus !== OUTBOUND_LETTER_SECURE_FILE_STATUS.READY &&
    override.basisTime === ((report && report.time) || null);
  if (!stillAsRetried) return own;
  return { secureFileToken: override.secureFileToken, secureFileStatus: override.secureFileStatus };
};

// `link` is `{ secureFileToken, secureFileStatus }` for the row.
// `secureFilesByToken` is the workflow's secure files keyed by share token, or
// `null` while they are not loaded (nothing extra is shown until they are, so a
// live link is never briefly shown as expired). A READY link whose file is
// revoked, past its expiry or deleted from the Secure File tab is "expired".
export const getOutboundLetterLinkState = (link, secureFilesByToken) => {
  const status = link && link.secureFileStatus;
  if (status === OUTBOUND_LETTER_SECURE_FILE_STATUS.FAILED) return OUTBOUND_LETTER_LINK_STATE.FAILED;
  if (status !== OUTBOUND_LETTER_SECURE_FILE_STATUS.READY || !link.secureFileToken || !secureFilesByToken) {
    return OUTBOUND_LETTER_LINK_STATE.NONE;
  }
  const file = secureFilesByToken[link.secureFileToken];
  return file && file.shareActive ? OUTBOUND_LETTER_LINK_STATE.ACTIVE : OUTBOUND_LETTER_LINK_STATE.EXPIRED;
};

// Returns the verified outbound letters on a `client_appointment` doc.
// Excludes entries that PMS has archived so the UI never shows files that no
// longer exist on the source system. Legacy entries that pre-date the
// `status` field (no value set) are kept — same defensive behaviour as the
// pre-archive logic.
export const getVerifiedOutboundLetters = (ca) => {
  const reports = (ca && ca.gp_reports) || [];
  if (!Array.isArray(reports)) return [];
  return reports.filter((report) => report && report.status !== OUTBOUND_LETTER_STATUS.ARCHIVED);
};
